'use client';

import { maskitoDateOptionsGenerator } from '@maskito/kit';
import { useMaskito } from '@maskito/react';
import { DateFormats } from '@repo/constants';
import {
  Label,
  Popover,
  PopoverContent,
  PopoverContentProps,
  PopoverTrigger,
  Skeleton,
  buttonVariants,
} from '@ui/components';
import { cn } from '@ui/lib/utils';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { FC, useCallback, useEffect, useMemo } from 'react';

dayjs.extend(customParseFormat);

const DEFAULT_MIN_MONTH = '1900-01';
const DEFAULT_MAX_MONTH = '2099-12';

const convertMonthToPresentation = (value: string) => {
  const parsed = dayjs(value, DateFormats.YEAR_MONTH, true);
  return parsed.isValid()
    ? parsed.format(DateFormats.YEAR_MONTH_PRESENTATION)
    : '';
};

interface MonthPickerProps {
  id?: string;
  className?: string;
  label?: string;
  placeholder?: string;
  minMonth?: string;
  maxMonth?: string;
  value?: string;
  onChange: (month: string) => void;
  disabled?: boolean;
  isSkeleton?: boolean;
  popoverContentProps?: PopoverContentProps;
}

export const MonthPicker: FC<MonthPickerProps> = (props) => {
  const {
    id,
    className,
    label = '',
    placeholder = 'MM/YYYY',
    value,
    minMonth = DEFAULT_MIN_MONTH,
    maxMonth = DEFAULT_MAX_MONTH,
    disabled,
    isSkeleton,
    onChange,
    popoverContentProps,
  } = props;

  const inputId = id ?? 'month-picker-input';

  const selectedMonthValue = useMemo(() => {
    const raw = value ?? '';
    const parsedDefault = dayjs(raw, DateFormats.DEFAULT, true);
    if (parsedDefault.isValid()) return parsedDefault.startOf('month');
    const parsedYearMonth = dayjs(raw, DateFormats.YEAR_MONTH, true);
    return parsedYearMonth.isValid() ? parsedYearMonth.startOf('month') : null;
  }, [value]);

  const selectedMonthString =
    selectedMonthValue?.format(DateFormats.YEAR_MONTH) ?? '';

  const minMonthValue = useMemo(() => {
    const parsed = dayjs(minMonth, DateFormats.YEAR_MONTH, true);
    return parsed.isValid()
      ? parsed.startOf('month')
      : dayjs(DEFAULT_MIN_MONTH, DateFormats.YEAR_MONTH).startOf('month');
  }, [minMonth]);

  const maxMonthValue = useMemo(() => {
    const parsed = dayjs(maxMonth, DateFormats.YEAR_MONTH, true);
    return parsed.isValid()
      ? parsed.startOf('month')
      : dayjs(DEFAULT_MAX_MONTH, DateFormats.YEAR_MONTH).startOf('month');
  }, [maxMonth]);

  const clampYear = useCallback(
    (year: number) =>
      Math.min(Math.max(year, minMonthValue.year()), maxMonthValue.year()),
    [minMonthValue, maxMonthValue],
  );

  const options = useMemo(
    () =>
      maskitoDateOptionsGenerator({
        mode: 'mm/yyyy',
        separator: '/',
        min: minMonthValue.toDate(),
        max: maxMonthValue.endOf('month').toDate(),
      }),
    [minMonthValue, maxMonthValue],
  );

  const inputRef = useMaskito({ options });

  const [isOpen, setIsOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const [currentYear, setCurrentYear] = React.useState(
    selectedMonthValue?.year() ?? clampYear(dayjs().year()),
  );

  useEffect(() => {
    setInputValue(convertMonthToPresentation(selectedMonthString));
    if (selectedMonthValue) {
      setCurrentYear(selectedMonthValue.year());
    } else {
      setCurrentYear(clampYear(dayjs().year()));
    }
  }, [selectedMonthValue, selectedMonthString, clampYear]);

  const handleOpen = useCallback(
    (nextIsOpen: boolean) => {
      if (disabled) return;
      setIsOpen(nextIsOpen);

      if (nextIsOpen) {
        const baseYear =
          selectedMonthValue?.year() ?? clampYear(dayjs().year());
        setCurrentYear(clampYear(baseYear));
      }
    },
    [disabled, selectedMonthValue, clampYear],
  );

  const handleChangeByInput = useCallback(() => {
    const parsed = dayjs(
      inputValue,
      DateFormats.YEAR_MONTH_PRESENTATION,
      true,
    ).startOf('month');

    if (
      parsed.isValid() &&
      !parsed.isBefore(minMonthValue, 'month') &&
      !parsed.isAfter(maxMonthValue, 'month')
    ) {
      onChange(parsed.endOf('month').format(DateFormats.DEFAULT));
      setCurrentYear(parsed.year());
    } else if (selectedMonthValue) {
      setInputValue(convertMonthToPresentation(selectedMonthString));
    } else {
      setInputValue('');
    }
  }, [
    inputValue,
    minMonthValue,
    maxMonthValue,
    onChange,
    selectedMonthValue,
    selectedMonthString,
  ]);

  const handleSelectMonth = useCallback(
    (monthIndex: number) => {
      const selected = dayjs()
        .set('year', currentYear)
        .set('month', monthIndex)
        .startOf('month');

      if (
        selected.isBefore(minMonthValue, 'month') ||
        selected.isAfter(maxMonthValue, 'month')
      ) {
        return;
      }

      setInputValue(selected.format(DateFormats.YEAR_MONTH_PRESENTATION));
      setCurrentYear(selected.year());
      onChange(selected.endOf('month').format(DateFormats.DEFAULT));
      setIsOpen(false);
    },
    [currentYear, minMonthValue, maxMonthValue, onChange],
  );

  const isPrevYearDisabled = useMemo(() => {
    const prevYearLastMonth = dayjs()
      .set('year', currentYear - 1)
      .set('month', 11)
      .startOf('month');

    return prevYearLastMonth.isBefore(minMonthValue, 'month');
  }, [currentYear, minMonthValue]);

  const isNextYearDisabled = useMemo(() => {
    const nextYearFirstMonth = dayjs()
      .set('year', currentYear + 1)
      .set('month', 0)
      .startOf('month');

    return nextYearFirstMonth.isAfter(maxMonthValue, 'month');
  }, [currentYear, maxMonthValue]);

  const monthNames = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) =>
        dayjs().set('month', index).format('MMM'),
      ),
    [],
  );

  return (
    <Popover
      open={isOpen}
      onOpenChange={handleOpen}
    >
      {isSkeleton ? (
        <Skeleton
          className={cn('w-[200px] h-10 text-left rounded-md', className)}
        />
      ) : (
        <PopoverTrigger
          disabled={disabled}
          asChild
        >
          <div
            className={cn(
              'flex flex-col gap-2',
              className,
              disabled && 'cursor-not-allowed',
            )}
          >
            {!!label && (
              <Label
                className="text-xs font-semibold"
                htmlFor={inputId}
              >
                {label}
              </Label>
            )}
            <div
              className={cn(
                'flex h-10 w-[200px] flex-row items-center justify-between overflow-hidden rounded-md border border-input bg-background pr-1 text-left font-normal hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none',
                className,
              )}
            >
              <input
                ref={inputRef}
                className={cn(
                  'flex flex-1 w-full bg-transparent outline-0 px-3',
                  disabled && 'cursor-not-allowed disabled:opacity-disabled',
                )}
                disabled={disabled}
                id={inputId}
                placeholder={placeholder}
                value={inputValue}
                onBlur={handleChangeByInput}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setInputValue(e.target?.value)
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleChangeByInput();
                  }
                }}
              />
              <div
                className={cn(
                  'cursor-pointer p-2',
                  disabled && 'cursor-not-allowed',
                )}
              >
                <CalendarIcon
                  className={cn(
                    'size-4 min-w-4',
                    disabled && 'opacity-disabled',
                  )}
                />
              </div>
            </div>
          </div>
        </PopoverTrigger>
      )}
      <PopoverContent
        {...popoverContentProps}
        className={cn('mx-4 w-auto p-0', popoverContentProps?.className)}
      >
        <div className="w-[224px] p-3">
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              className={cn(
                buttonVariants({ variant: 'outline' }),
                'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
                isPrevYearDisabled && 'pointer-events-none opacity-30',
              )}
              onClick={() => setCurrentYear((year) => year - 1)}
              disabled={isPrevYearDisabled}
            >
              <ChevronLeft className="size-4" />
            </button>
            <span className="text-sm font-medium">{currentYear}</span>
            <button
              type="button"
              className={cn(
                buttonVariants({ variant: 'outline' }),
                'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
                isNextYearDisabled && 'pointer-events-none opacity-30',
              )}
              onClick={() => setCurrentYear((year) => year + 1)}
              disabled={isNextYearDisabled}
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {monthNames.map((monthName, index) => {
              const monthDate = dayjs()
                .set('year', currentYear)
                .set('month', index)
                .startOf('month');

              const isSelected =
                selectedMonthValue?.year() === currentYear &&
                selectedMonthValue?.month() === index;

              const isDisabled =
                monthDate.isBefore(minMonthValue, 'month') ||
                monthDate.isAfter(maxMonthValue, 'month');

              return (
                <button
                  key={monthName}
                  type="button"
                  className={cn(
                    buttonVariants({ variant: 'ghost' }),
                    'h-9 justify-center',
                    isSelected &&
                      'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                    isDisabled &&
                      'text-muted-foreground opacity-50 cursor-not-allowed',
                  )}
                  disabled={isDisabled}
                  onClick={() => handleSelectMonth(index)}
                >
                  {monthName}
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
